import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import useScreenWidth from "../../lib/useGetScreenWidth";
import { PencilSimpleLine, Plus } from "@phosphor-icons/react";
import { iconSize } from "../../const/sizes";
import { useFormik } from "formik";
import RequiredForm from "../RequiredForm";
import Textarea from "../Textarea";
import FileUploader from "../FileUploader";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import useSilabusUploadProccesses from "../../globalState/useSilabusUploadProcesses";
import { useState } from "react";
import { Silabus } from "../../const/types";
import cr from "../../lib/request";
import useTrigger from "../../globalState/useTrigger";
import useDataChanges from "../../globalState/useDataChanges";

type Props = {
  data: Silabus;
};

export default function UpdateSilabusModal({ data }: Props) {
  const sw = useScreenWidth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { id } = useParams();
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const { trigger, setTrigger } = useTrigger();
  const toast = useToast();
  const { setSilabusUploadProccesses, silabusUploadProccesses } =
    useSilabusUploadProccesses();
  const { setDataChanges } = useDataChanges();
  const [inputVideoUrl, setInputVideoUrl] = useState<boolean>(true);

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      nama_sub_course: data.nama_sub_course,
      description: data.description,
      yt_url: data.yt_url,
      content_link: [] as File[] | null,
      thumbnail: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      nama_sub_course: yup.string().required("Judul Silabus harus diisi"),
      description: yup.string().required("Deskripsi Silabus harus diisi"),
      yt_url: inputVideoUrl
        ? yup.string().required("URL harus diisi")
        : yup.mixed(),
      content_link: !inputVideoUrl
        ? yup
            .array()
            .min(1, "Video harus diunggah")
            .required("Video harus diunggah")
            .test(
              "fileType",
              "Hanya file MP4, AVI, MOV yang diperbolehkan",
              (value) =>
                value.every(
                  (file) =>
                    file.type === "video/mp4" ||
                    file.type === "video/avi" ||
                    file.type === "video/quicktime"
                )
            )
        : yup.mixed(),
    }),

    onSubmit: async (values, { resetForm }) => {
      // console.log(values);

      const payload = new FormData();
      if (id) {
        payload.append("course_id", id);
      }
      payload.append("nama_sub_course", values.nama_sub_course);
      payload.append("description", values.description);
      payload.append("yt_url", values.yt_url);
      if (values.content_link) {
        values.content_link.forEach((file) => {
          payload.append("content_link", file);
        });
      }
      if (values.thumbnail) {
        values.thumbnail.forEach((file) => {
          payload.append("thumbnail", file);
        });
      }

      if (values.content_link && values.content_link.length > 0) {
        const newSilabusUploadProccess = {
          id: new Date(),
          title: values.nama_sub_course,
          payload: payload,
          status: "update",
          data_id: data.id,
        };

        setSilabusUploadProccesses([
          ...silabusUploadProccesses,
          newSilabusUploadProccess,
        ]);
        onClose();
      } else {
        setLoading(true);
        const url = `api/subcourse/${data.id}/updatesubcourse`;
        cr.post(url, payload)
          .then((r) => {
            if (r.status === 200) {
              resetForm();
              toast({
                title: r?.data?.message,
                status: "success",
                duration: 5000,
                isClosable: true,
              });
              setTrigger(!trigger);
              setDataChanges(data.id);
            }
          })
          .catch((e) => {
            console.log(e);
            toast({
              title: "Gagal update silabus",
              description: e?.response?.data?.message,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
  });

  return (
    <>
      {sw >= 500 ? (
        <Button
          colorScheme="ap"
          variant={"ghost"}
          className="clicky"
          leftIcon={<Icon as={PencilSimpleLine} fontSize={iconSize} />}
          pl={3}
          onClick={onOpen}
          flexShrink={0}
        >
          Update
        </Button>
      ) : (
        <IconButton
          aria-label="button tambah"
          colorScheme="p"
          className="clicky"
          icon={<Icon as={Plus} fontSize={20} />}
          onClick={onOpen}
        />
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>Update Silabus Course</ModalHeader>
          <ModalBody>
            <Image
              src={
                thumbnailPreviewUrl
                  ? thumbnailPreviewUrl
                  : `${process.env.REACT_APP_MEDIA_BASE_URL}${data.thumbnail}` ||
                    "/images/thumbnailDefault.png"
              }
              w={"100%"}
              h={"200px"}
              objectFit={"cover"}
              mb={4}
              border={"1px solid var(--divider3)"}
            />

            <form id="updateCourseForm" onSubmit={formik.handleSubmit}>
              <FormControl
                isInvalid={formik.errors.thumbnail ? true : false}
                mb={4}
              >
                <FormLabel>Gambar Thumbnail</FormLabel>
                <FileUploader
                  name="thumbnail"
                  formik={formik}
                  accept=".png, .jpg, .jpeg, .heic"
                  setPreviewUrl={setThumbnailPreviewUrl}
                />
                <FormErrorMessage>{formik.errors.thumbnail}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.nama_sub_course ? true : false}
              >
                <FormLabel>
                  Judul
                  <RequiredForm />
                </FormLabel>
                <Textarea
                  name="nama_sub_course"
                  placeholder="Masukkan judul silabus"
                  formik={formik}
                />

                <FormErrorMessage>
                  {formik.errors.nama_sub_course}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.description ? true : false}
              >
                <FormLabel>
                  Deskripsi
                  <RequiredForm />
                </FormLabel>
                <Textarea
                  name="description"
                  placeholder="Masukkan deskripsi"
                  formik={formik}
                />

                <FormErrorMessage>{formik.errors.description}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={
                  inputVideoUrl
                    ? formik.errors.yt_url
                      ? true
                      : false
                    : formik.errors.content_link
                    ? true
                    : false
                }
              >
                <FormLabel fontWeight={500} mb={2}>
                  Video
                  <RequiredForm />
                </FormLabel>

                <HStack mb={2}>
                  <Button
                    onClick={() => {
                      setInputVideoUrl(true);
                    }}
                    className="btn"
                    border={"1px solid var(--divider3)"}
                    bg={inputVideoUrl ? "var(--divider) !important" : ""}
                  >
                    URL
                  </Button>
                  <Button
                    onClick={() => {
                      setInputVideoUrl(false);
                    }}
                    className="btn"
                    border={"1px solid var(--divider3)"}
                    bg={inputVideoUrl ? "" : "var(--divider) !important"}
                  >
                    File
                  </Button>
                </HStack>

                {inputVideoUrl ? (
                  <Textarea
                    name="yt_url"
                    formik={formik}
                    placeholder="Masukkan video url"
                  />
                ) : (
                  <Text>Fitur unggah file belum tersedia</Text>
                )}
                {/* <>
                  <Alert
                    variant={"left-accent"}
                    mb={2}
                    colorScheme="orange"
                    borderRadius={8}
                  >
                    <AlertDescription>
                      Untuk update video menggunakan file, hapus dahulu kolom
                      URL kemudian unggah filenya, karena URL yang diutamakan
                    </AlertDescription>
                  </Alert>

                  <FileUploader name="content_link" formik={formik} />
                </> */}

                <FormErrorMessage>
                  {inputVideoUrl
                    ? formik.errors.yt_url
                    : formik.errors.content_link}
                </FormErrorMessage>
              </FormControl>
            </form>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button className="btn clicky" onClick={onClose}>
              Batal
            </Button>

            <Button
              type="submit"
              form="updateCourseForm"
              colorScheme="p"
              className="clicky"
              isLoading={loading}
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
